import React from "react";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Chip,
  CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import ViewListIcon from "@mui/icons-material/ViewList";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import ArchiveIcon from "@mui/icons-material/Archive";
import DeleteIcon from "@mui/icons-material/Delete";
import RouteIcon from "@mui/icons-material/Route";
import PieChartIcon from "@mui/icons-material/PieChart";
import CancelIcon from "@mui/icons-material/Cancel";
import AssessmentIcon from "@mui/icons-material/Assessment";
import RefreshIcon from "@mui/icons-material/Refresh";
import SendIcon from "@mui/icons-material/Send";
import { tradeBlotterFilterControlSx, tradeBlotterOutlinedActionSx } from "../tradeBlotterStyles";

const ToolbarBadge = ({ visible, color = "error" }) =>
  visible ? (
    <Box
      sx={{
        position: "absolute",
        top: -4,
        right: -4,
        backgroundColor: (theme) => theme.palette[color].main,
        color: "white",
        borderRadius: "50%",
        width: 18,
        height: 18,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "10px",
        fontWeight: 600,
      }}
    >
      !
    </Box>
  ) : null;

const selectMenuProps = {
  disableAutoFocusItem: true,
  disableRestoreFocus: true,
};

const blurActiveElement = () => {
  if (document.activeElement instanceof HTMLElement) {
    document.activeElement.blur();
  }
};

const TradeBlotterToolbar = ({
  theme,
  searchTerm,
  onSearchChange,
  selectedEntity,
  onEntityChange,
  uniqueFunds,
  selectedStrategy,
  onStrategyChange,
  uniqueStrategies,
  selectedTicker,
  onTickerChange,
  uniqueTickers,
  viewType,
  onViewTypeChange,
  tableDataLength,
  onSelectedDateChange,
  dateInputValue,
  onTodayClick,
  dataGridRef,
  hasActiveFilters,
  flatChildView,
  onFlatChildViewChange,
  isUpdateFillsLoading,
  hasSsandcFillsRefreshTargets,
  onUpdateFills,
  onViewSummary,
  onAutoFillCommissions,
  hasCommissionEligibleTradesForDate,
  onUpdateSelected,
  onDeleteSelected,
  onArchiveSelected,
  onRouteSelect,
  onAllocationSubmit,
  allocationEligibleCount,
  onCancelTradesSubmit,
  cancelEligibleCount,
  selectedTradesCount,
  onNewOrder,
  canShowSendPatsButton,
  hasOnlyPatsEligibleSelection,
  sendingPats,
  canSendPats,
  onSendPats,
}) => {
  const filterControlSx = tradeBlotterFilterControlSx(theme);
  const toggleGroupSx = {
    borderRadius: 1,
    "& .MuiToggleButton-root": {
      borderRadius: "4px !important",
      px: 1,
      py: 0.5,
      borderColor: theme.palette.divider,
      color: theme.palette.text.primary,
      "&.Mui-selected": {
        color: theme.palette.primary.main,
        backgroundColor: theme.palette.action.selected,
        "&:hover": { backgroundColor: theme.palette.action.selected },
      },
    },
  };

  return (
    <Box
      sx={{
        p: 3,
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <TextField
          size="small"
          placeholder="Search tickers, funds, or strategies..."
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: theme.palette.text.disabled }} />
              </InputAdornment>
            ),
          }}
          sx={{
            width: 400,
            "& .MuiOutlinedInput-root": {
              borderRadius: 1,
              backgroundColor: theme.palette.background.paper,
              "& fieldset": { borderColor: theme.palette.divider },
              "&:hover fieldset": { borderColor: theme.palette.primary.main },
            },
          }}
        />

        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Chip
            label={`${viewType === "deleted" ? "Deleted" : "Active"} Trades (${tableDataLength})`}
            color={viewType === "deleted" ? "error" : "success"}
            size="small"
            variant="outlined"
          />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel sx={{ color: theme.palette.text.secondary }}>Trade View</InputLabel>
            <Select
              value={viewType}
              onChange={(e) => onViewTypeChange(e.target.value)}
              onClose={blurActiveElement}
              MenuProps={selectMenuProps}
              label="Trade View"
              sx={filterControlSx}
            >
              <MenuItem value="non-deleted">Active Trades</MenuItem>
              <MenuItem value="deleted">Deleted Trades</MenuItem>
            </Select>
          </FormControl>
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontSize: "0.7rem",
              maxWidth: 200,
              lineHeight: 1.2,
            }}
          >
            Switch between active and deleted trades for the selected date
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel sx={{ color: theme.palette.text.secondary }}>FUNDS</InputLabel>
            <Select
              value={selectedEntity}
              onChange={(e) => onEntityChange(e.target.value)}
              onClose={blurActiveElement}
              MenuProps={selectMenuProps}
              label="FUNDS"
              sx={filterControlSx}
            >
              <MenuItem value="">Select an option</MenuItem>
              {uniqueFunds.map((fund) => (
                <MenuItem key={fund} value={fund}>
                  {fund}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel sx={{ color: theme.palette.text.secondary }}>STRATEGIES</InputLabel>
            <Select
              value={selectedStrategy}
              onChange={(e) => onStrategyChange(e.target.value)}
              onClose={blurActiveElement}
              MenuProps={selectMenuProps}
              label="STRATEGIES"
              sx={filterControlSx}
            >
              <MenuItem value="">Select an option</MenuItem>
              {uniqueStrategies.map((strategy) => (
                <MenuItem key={strategy} value={strategy}>
                  {strategy}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel sx={{ color: theme.palette.text.secondary }}>TICKERS</InputLabel>
            <Select
              value={selectedTicker}
              onChange={(e) => onTickerChange(e.target.value)}
              onClose={blurActiveElement}
              MenuProps={selectMenuProps}
              label="TICKERS"
              sx={filterControlSx}
            >
              <MenuItem value="">Select an option</MenuItem>
              {uniqueTickers.map((ticker) => (
                <MenuItem key={ticker} value={ticker}>
                  {ticker}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ minWidth: 100 }}>
            <TextField
              type="date"
              label="DATE"
              size="small"
              fullWidth
              value={dateInputValue}
              onChange={(e) => {
                const [year, month, day] = e.target.value.split("-").map(Number);
                onSelectedDateChange(new Date(year, month - 1, day));
              }}
              sx={filterControlSx}
            />
          </Box>
          <Button
            size="small"
            variant="outlined"
            onClick={onTodayClick}
            sx={{ borderRadius: 1, textTransform: "none", fontWeight: 500, px: 2 }}
          >
            Today
          </Button>
          <Tooltip title="Advanced Filters">
            <IconButton
              size="small"
              onClick={() => dataGridRef.current?.toggleFilterPanel?.()}
              sx={{
                borderRadius: 1,
                color: theme.palette.text.primary,
                position: "relative",
                "&:hover": { backgroundColor: theme.palette.action.hover },
              }}
            >
              <FilterListIcon />
              <ToolbarBadge visible={hasActiveFilters} />
            </IconButton>
          </Tooltip>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={flatChildView ? "flat" : "hierarchy"}
            onChange={(_, value) => value !== null && onFlatChildViewChange(value === "flat")}
            sx={toggleGroupSx}
          >
            <Tooltip title="Parent/child view" arrow placement="top">
              <ToggleButton value="hierarchy" aria-label="Parent/child view">
                <AccountTreeIcon fontSize="small" />
              </ToggleButton>
            </Tooltip>
            <Tooltip title="Flat view (child trades as rows)" arrow placement="top">
              <ToggleButton value="flat" aria-label="Flat view">
                <ViewListIcon fontSize="small" />
              </ToggleButton>
            </Tooltip>
          </ToggleButtonGroup>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Tooltip
            title={
              isUpdateFillsLoading
                ? "Refreshing…"
                : !hasSsandcFillsRefreshTargets
                  ? "No SS&C trades in the current view to refresh"
                  : "Refresh"
            }
            arrow
            placement="bottom"
          >
            <span>
              <IconButton
                size="small"
                onClick={onUpdateFills}
                disabled={isUpdateFillsLoading || !hasSsandcFillsRefreshTargets}
                aria-label="Refresh"
                sx={{
                  width: 36,
                  height: 36,
                  border: `1px solid ${theme.palette.divider}`,
                  color: theme.palette.primary.main,
                  backgroundColor: theme.palette.background.paper,
                  "&:hover": {
                    backgroundColor: theme.palette.action.hover,
                    borderColor: theme.palette.primary.light,
                  },
                }}
              >
                {isUpdateFillsLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <RefreshIcon sx={{ fontSize: 20 }} />
                )}
              </IconButton>
            </span>
          </Tooltip>

          <Button
            variant="outlined"
            startIcon={<AssessmentIcon />}
            onClick={onViewSummary}
            disabled={tableDataLength === 0}
            sx={tradeBlotterOutlinedActionSx(theme, "#009688", "#4db6ac")}
          >
            View Summary
          </Button>
          <Button
            variant="outlined"
            startIcon={<AutoFixHighIcon />}
            onClick={onAutoFillCommissions}
            disabled={!hasCommissionEligibleTradesForDate}
            sx={tradeBlotterOutlinedActionSx(theme, "#ff9800", "#ffb74d")}
          >
            Auto Fill Commissions
          </Button>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={onUpdateSelected}
            disabled={selectedTradesCount === 0}
            sx={tradeBlotterOutlinedActionSx(theme, "#d8e10d", "#00ff88", {
              hoverTextColor: "#000000",
              hoverGradientEnd: "#00cc6a",
            })}
          >
            Update Selected
          </Button>
          <Button
            variant="outlined"
            startIcon={<DeleteIcon />}
            onClick={onDeleteSelected}
            disabled={selectedTradesCount === 0}
            sx={tradeBlotterOutlinedActionSx(theme, "#d32f2f", "#ef5350", {
              disabledUsesMutedBorder: true,
            })}
          >
            Delete Selected
          </Button>
          {/* <Button
            variant="outlined"
            startIcon={<ArchiveIcon />}
            onClick={onArchiveSelected}
            disabled={selectedTradesCount === 0}
            sx={tradeBlotterOutlinedActionSx(theme, "#1976d2", "#42a5f5", {
              disabledUsesMutedBorder: true,
            })}
          >
            Archive Selected
          </Button> */}
          <Button
            variant="outlined"
            startIcon={<RouteIcon />}
            onClick={onRouteSelect}
            disabled={selectedTradesCount === 0}
            sx={tradeBlotterOutlinedActionSx(theme, "#4caf50", "#81c784")}
          >
            Route Selected
          </Button>
          <Tooltip title="Allocate selected SS&C routed trades that have no allocation yet" arrow placement="bottom">
            <span>
              <Button
                variant="outlined"
                startIcon={<PieChartIcon />}
                onClick={onAllocationSubmit}
                disabled={allocationEligibleCount === 0}
                sx={tradeBlotterOutlinedActionSx(theme, "#7b1fa2", "#ba68c8")}
              >
                Send SS&C allocations
              </Button>
            </span>
          </Tooltip>
          {/* <Tooltip title="Cancel selected trades that are routed via SS&C" arrow placement="bottom">
            <span>
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={onCancelTradesSubmit}
                disabled={cancelEligibleCount === 0}
                sx={tradeBlotterOutlinedActionSx(theme, "#c62828", "#ef5350")}
              >
                Cancel Trades
              </Button>
            </span>
          </Tooltip> */}
          {canShowSendPatsButton && (
            <Tooltip
              title={
                !hasOnlyPatsEligibleSelection
                  ? "Only Private trades routed to the PATS broker can be sent from this action"
                  : sendingPats
                    ? "Sending trades to PATS..."
                    : "Send selected trades to PATS"
              }
              arrow
              placement="bottom"
            >
              <span>
                <Button
                  variant="outlined"
                  startIcon={<SendIcon />}
                  onClick={onSendPats}
                  disabled={!canSendPats}
                  sx={tradeBlotterOutlinedActionSx(theme, "#00acc1", "#4dd0e1")}
                >
                  {sendingPats ? "Sending..." : "Send to PATS"}
                </Button>
              </span>
            </Tooltip>
          )}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onNewOrder}
            sx={{
              borderRadius: 1.5,
              textTransform: "none",
              fontWeight: 600,
              px: 3,
              backgroundColor: theme.palette.primary.main,
              color: "#ffffff",
              "&:hover": { backgroundColor: theme.palette.primary.dark },
            }}
          >
            New order
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default TradeBlotterToolbar;
